import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCalendarDays,
  faGear,
  faPalette,
  faPlugCircleBolt,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Permissions, useHasPermission } from '@/core/permissions/export';
import { useIsMobile } from '@/shared/hooks/export';
import { MobileFullscreenPanel } from '@/shared/components/export';
import ConnectionTab from '@/core/settings/components/connection-tab';
import AppearanceTab from '@/core/settings/components/appearance-tab';
import CalendarTimezoneTab from '@/core/settings/components/calendar-timezone-tab';
import NotificationsTab from '@/core/settings/components/notifications-tab';
import TabButtonRow from '@/core/settings/components/tab-button-row';
import { NotificationsSubTab, SettingsTab } from '@/core/widget-navigation/export';
import styles from './settings-drawer.module.css';

const TABS: { key: SettingsTab; label: string; icon: IconDefinition }[] = [
  { key: 'connection', label: 'Connection', icon: faPlugCircleBolt },
  { key: 'appearance', label: 'Appearance', icon: faPalette },
  { key: 'calendar', label: 'Calendar and TZ', icon: faCalendarDays },
  { key: 'notifications', label: 'Notifications', icon: faBell },
];

export default function SettingsDrawer({
  isOpen,
  tab,
  subtab,
  onClose,
  onTabChange,
  onSubtabChange,
}: {
  isOpen: boolean;
  tab: SettingsTab;
  subtab: NotificationsSubTab;
  onClose: () => void;
  onTabChange: (tab: SettingsTab) => void;
  onSubtabChange: (subtab: NotificationsSubTab) => void;
}) {
  const hasNotificationsPermission = useHasPermission(Permissions.NOTIFICATIONS);
  const isMobile = useIsMobile(768);

  const visibleTabs = TABS.filter(t => t.key !== 'notifications' || hasNotificationsPermission);
  const activeTab = visibleTabs.some(t => t.key === tab) ? tab : 'connection';

  const titleContent = (
    <span className={styles.title}>
      <FontAwesomeIcon icon={faGear} className="me-2" />
      Settings
    </span>
  );

  const tabBar = (
    <TabButtonRow
      items={visibleTabs}
      activeKey={activeTab}
      onSelect={onTabChange}
      orientation="horizontal"
      compact={isMobile}
    />
  );

  const body = (
    <>
      {isOpen && activeTab === 'connection' && <ConnectionTab />}
      {isOpen && activeTab === 'appearance' && <AppearanceTab />}
      {isOpen && activeTab === 'calendar' && <CalendarTimezoneTab />}
      {isOpen && activeTab === 'notifications' && (
        <NotificationsTab subtab={subtab} onSubtabChange={onSubtabChange} />
      )}
    </>
  );

  if (isMobile) {
    if (!isOpen) return null;
    return (
      <MobileFullscreenPanel header={titleContent} subHeader={tabBar} onClose={onClose}>
        {body}
      </MobileFullscreenPanel>
    );
  }

  return (
    <>
      {isOpen && <div className={styles.backdrop} onClick={onClose} />}
      <div
        className={styles.content}
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <div className={styles.contentHeader}>
          {titleContent}
          <button type="button" className="btn btn-sm text-muted" onClick={onClose} title="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {tabBar}

        <div className={styles.contentBody}>{body}</div>
      </div>
    </>
  );
}
